import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, API_BASE_URL, describeApiError } from '@/lib/api-client';
import { tokenStorage } from '@/utils/token-storage';
import { useToast } from '@/hooks/use-toast';

// Types for contracts list
export interface ContractsListParams {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    /** Grouped list filter. Default on the list page is `active`. */
    outcome?: 'active' | 'lost' | 'won' | 'all';
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

export interface ContractsListResponse {
    contracts: ContractListItem[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasNext: boolean;
        hasPrev: boolean;
    };
}

export interface ContractListItem {
    id: string;
    contractId: string;
    projectId: string;
    proposalId: string;
    createdBy: string;
    organizationId: string;
    /** Locked customer price at execution (WON); null while still DRAFT/pending. */
    totalContractValue?: number | null;
    /** Flattened by GET /contracts — works when contractData is a seed stub. */
    clientName?: string | null;
    projectLabel?: string | null;
    displayValue?: number | null;
    /** Human title (project name / change-order name). */
    contractTitle?: string | null;
    /** ISO date when signed; null while unsigned. */
    signedAt?: string | null;
    executedAt?: string | null;
    paperExecutedAt?: string | null;
    companySignedAt?: string | null;
    /** User-visible project code (`P26-1001`), not a UUID. */
    projectCode?: string | null;
    fieldCode?: string | null;
    contractData: {
        client: {
            fullName: string;
            businessName?: string;
            clientType?: string;
        };
        project: {
            projectLabel: string;
            investmentMin: number;
            investmentMax: number;
            jobsiteAddress?: {
                city: string;
                state: string;
                addressLine1?: string;
            };
        };
    };
    contractPdfUrl: string | null;
    status: string;
    createdAt: string;
    updatedAt: string;
    project: any;
    proposal: any;
    creator: {
        id: string;
        fullName: string;
        email: string;
    };
}

// Hook for listing contracts with pagination/search/filter/sort
export const useContractsList = (
    params: ContractsListParams = {},
    options: { enabled?: boolean } = {},
) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.search) queryParams.set('search', params.search);
    if (params.status && params.status !== 'ALL') queryParams.set('status', params.status);
    if (params.outcome) queryParams.set('outcome', params.outcome);
    if (params.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);

    return useQuery<ContractsListResponse, Error>({
        queryKey: ['contracts-list', params],
        queryFn: async () => {
            const qs = queryParams.toString();
            return apiClient.get<ContractsListResponse>(`/contracts${qs ? `?${qs}` : ''}`);
        },
        staleTime: 30 * 1000,
        enabled: options.enabled ?? true,
    });
};

// Types for contract creation
export interface CreateContractRequest {
    proposalId: string;
    userId: string;
}

export interface CreateContractResponse {
    contract: {
        id: string;
        contractId: string;
        proposalId: string;
        userId: string;
        status: string;
        createdAt: string;
        updatedAt: string;
    }
}

// Types for contract email sending
export interface SendContractEmailRequest {
    contractId: string;
    isReminder?: boolean;
    /** IANA timezone of the person clicking Send (browser local time). */
    senderTimeZone?: string;
}

function browserTimeZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
    } catch {
        return 'America/New_York';
    }
}

export interface SendContractEmailResponse {
    success: boolean;
    message: string;
    sentTo?: string;
    contractId?: string;
    contractLink?: string;
    messageId?: string;
    attachedPdf?: boolean;
}

// Types for contract signing
export interface SignatureData {
    signerType: 'CLIENT' | 'COMPANY';
    agreementToTerms: boolean;
    esignConsent?: boolean;
    clientName?: string; // Required for CLIENT type
    userId?: string; // Required for COMPANY type
    clauseInitials?: Array<{ clauseId: string; text: string }>;
}

export interface SignContractRequest {
    contractId: string;
    signatures: File[]; // Array of signature image files
    signatureData: SignatureData[]; // Array of signature metadata
}

export interface SignContractResponse {
    success: boolean;
    message: string;
    contractId: string;
}

// Types for contract PDF generation
export interface GenerateContractPDFRequest {
    proposalId: string;
    userId: string; // Sales rep ID
}

export interface GenerateContractPdfByIdRequest {
    contractId: string;
}

export interface GenerateContractPDFResponse {
    success: boolean;
    message: string;
    pdfUrl: string;
    contractId: string;
    id?: string;
}

// Types for contract details
export interface ContractDetails {
    id: string;
    contractId: string;
    projectId: string;
    proposalId: string;
    createdBy: string;
    organizationId: string;
    contractData: {
        client: {
            id: string;
            city: string;
            email: string;
            phone: string;
            state: string;
            address: string;
            zipCode: string;
            fullName: string;
            clientType: string;
            businessName: string;
        };
        project: {
            id: string;
            status: string;
            summary: string;
            elements: Array<{
                id: string;
                data: any;
                elementName: string;
                categoryName: string;
            }>;
            siteNotes: string;
            projectLabel: string;
            estimatePhase: string;
            investmentMax: number;
            investmentMin: number;
            permitDueDate: string;
            renderingType: string;
            jobsiteAddress: {
                city: string;
                state: string;
                lotNumber: string;
                postalCode: string;
                addressLine1: string;
                addressLine2: string;
            };
            specialRequests: string;
            presentationDate: string;
            projectDocumentId: string;
            renderingRequired: boolean;
            permitDocumentation: string;
        };
        uploads: Array<{
            id: string;
            source: string;
            fileUrl: string;
            category: string;
            fileName: string;
            uploadType: string;
            description: string;
        }>;
        legal?: {
            contractLanguage?: string | null;
            limitedLiabilityWarranty?: string | null;
            overviewParagraph?: string | null;
        };
        /** Seed stubs store a warranty sentence here instead of legal.limitedLiabilityWarranty. */
        warranty?: string | null;
        display?: {
            preparedDate?: string | null;
            effectiveDate?: string | null;
            designConsultant?: string | null;
            designConsultantEmail?: string | null;
            licenseNumber?: string | null;
            licenseClassification?: string | null;
            licenseIssuingState?: string | null;
            premisesJurisdiction?: string | null;
            overviewParagraph?: string | null;
            weeksUntilStart?: string | null;
            weeksToComplete?: string | null;
            tentativeStartDate?: string | null;
            allowances?: Array<{
                type?: string | null;
                description?: string | null;
                amount?: number | string | null;
            }>;
        };
        transactionDate?: string;
        projectScope?: {
            statements?: Array<{
                scfCode?: string | null;
                groupLabel?: string | null;
                text: string;
                source?: string | null;
            }>;
            fields?: Array<{ scfCode: string; scfLabel: string; contractLabel?: string | null }>;
        } | null;
        csfScope?: Array<{
            n: number;
            heading: string;
            clusters: Array<{
                groupLabel: string;
                rows: Array<{ n: number; label: string | null; value: string }>;
            }>;
        }>;
        scope?: Array<{
            id?: string;
            elementTypeDisplayName?: string;
            instanceLabel?: string | null;
            sellPrice?: number;
            statements?: Array<{
                scfCode?: string | null;
                groupLabel?: string | null;
                text: string;
                source?: string | null;
            }>;
            fields?: Array<{ scfCode: string; scfLabel: string; contractLabel?: string | null }>;
        }>;
        investmentSummary?: {
            total?: number;
            finalTotal?: number;
            cashTotal?: number;
        } | null;
        decisions?: {
            financingProgramId?: string | null;
            financingConfirmation?: string | null;
            downPaymentKind?: 'nothing' | 'amount' | null;
            downPaymentAmount?: number | null;
            downPaymentMethod?: string | null;
            checkNumber?: string | null;
            cardConfirmation?: string | null;
        };
        proposal: {
            id: string;
            summary: string;
            paymentDetails: Array<{
                amount: number;
                milestones: string;
                paymentType: string;
            }>;
            proposalPdfUrl: string | null;
            proposalStatus: string;
            contractLanguage: string;
            weekUntilProjectEnds: string;
            assumptionsExclusions: string;
            weekUntilProjectStarts: string;
            limitedLiabilityWarranty: string;
            tentativeStartDate?: string | null;
            allowances?: Array<{
                type?: string | null;
                description?: string | null;
                amount?: number | string | null;
            }>;
        };
        templates?: Array<{
            id: string;
            name: string;
            type: string;
            content: string;
        }>;
        companyLogo: string | null;
        organization: {
            id: string;
            type: string;
            profile: {
                id: string;
                city: string;
                name: string;
                phone: string;
                state: string;
                address: string;
                logoUrl: string | null;
                website: string;
                zipCode: string;
                timezone: string;
                createdAt: string;
                updatedAt: string;
                organizationId: string;
                licenseNumber?: string | null;
                licenseClassification?: string | null;
                licenses?: Array<{
                    licenseNumber: string;
                    licenseClassification: string;
                    licenseLocation?: string | null;
                    issuingState?: string | null;
                }>;
            };
            businessEmail: string;
        };
        calculationResults: Array<{
            id: string;
            data: any;
            elementName: string;
            categoryName: string;
        }>;
    };
    contractPdfUrl: string | null;
    contractLanguage?: string | null;
    limitedLiabilityWarranty?: string | null;
    clientSignatures: Array<any>;
    companySignedAt: string | null;
    companySignature: string | null;
    companySignedBy: string | null;
    status: string;
    createdAt: string;
    updatedAt: string;
    project: any;
    proposal: any;
    creator: {
        id: string;
        fullName: string;
        email: string;
    };
    companySigner: any;
}

// Hook for fetching contract details
export const useContractDetails = (contractId: string) => {
    return useQuery<ContractDetails, Error>({
        queryKey: ['contract-details', contractId],
        queryFn: async () => {
            const response = await apiClient.get<ContractDetails>(`/contracts/${contractId}`);
            return response;
        },
        enabled: !!contractId,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
    });
};

// Hook for creating a contract
export const useCreateContract = () => {
    const { toast } = useToast();

    return useMutation<CreateContractResponse, Error, CreateContractRequest>({
        mutationFn: async (contractData: CreateContractRequest) => {
            const response = await apiClient.post<CreateContractResponse>('/contracts', contractData);
            return response;
        },
        onSuccess: () => {
            toast({
                title: "Contract Created",
                description: "Contract has been successfully created and shared with the client.",
            });
        },
        onError: (error) => {
            console.error('Failed to create contract:', error);
            toast({
                title: "Error",
                description: "Failed to create contract. Please try again.",
                variant: "destructive",
            });
        },
    });
};

/** Modern create — mint (or return existing) DRAFT from finalized snapshot. */
export interface CreateContractFromProjectResponse {
    success: boolean;
    message: string;
    contract: {
        id: string;
        contractId: string;
        projectId: string;
        status: string;
        createdAt: string;
    };
}

export const useCreateContractFromProject = () => {
    const { toast } = useToast();

    return useMutation<CreateContractFromProjectResponse, Error, string>({
        mutationFn: async (projectId: string) => {
            return apiClient.post<CreateContractFromProjectResponse>(
                `/contracts/from-project/${encodeURIComponent(projectId)}`,
            );
        },
        onError: (error) => {
            console.error('Failed to create contract from project:', error);
            toast({
                title: 'Could not create contract',
                description:
                    error instanceof Error
                        ? error.message
                        : 'Finalize the estimate and ensure company contract language is configured in Settings.',
                variant: 'destructive',
            });
        },
    });
};

/** List contracts for one project (Contracts module handoff / open CTA). */
export const useContractsByProject = (
    projectId: string,
    options: { enabled?: boolean } = {},
) => {
    return useQuery<ContractListItem[], Error>({
        queryKey: ['contracts-by-project', projectId],
        queryFn: async () => {
            const response = await apiClient.get<ContractListItem[] | { contracts: ContractListItem[] }>(
                `/contracts/project/${encodeURIComponent(projectId)}`,
            );
            return Array.isArray(response)
                ? response
                : (response?.contracts ?? []);
        },
        enabled: (options.enabled ?? true) && !!projectId,
        staleTime: 30 * 1000,
    });
};

// Hook for sending contract email
export const useSendContractEmail = () => {
    const { toast } = useToast();

    return useMutation<SendContractEmailResponse, Error, SendContractEmailRequest>({
        mutationFn: async (emailData: SendContractEmailRequest) => {
            const response = await apiClient.post<SendContractEmailResponse>('/contracts/send-email', {
                ...emailData,
                senderTimeZone: emailData.senderTimeZone || browserTimeZone(),
            });
            return response;
        },
        onSuccess: (data) => {
            toast({
                title: 'Email Sent',
                description: data.sentTo
                    ? `Sent to ${data.sentTo}. They can review and sign from the link.`
                    : 'Contract email has been sent to the client.',
            });
        },
        onError: (error) => {
            console.error('Failed to send contract email:', error);
            toast({
                title: "Error",
                description: "Failed to send contract email. Please try again.",
                variant: "destructive",
            });
        },
    });
};

/** Multipart body for POST /contracts/sign (staff JWT, not the public portal). */
export function buildStaffSignFormData(signData: SignContractRequest): FormData {
    const formData = new FormData();
    formData.append('contractId', signData.contractId);
    signData.signatures.forEach((signature) => {
        formData.append('signatures', signature);
    });
    formData.append('signatureData', JSON.stringify(signData.signatureData));
    return formData;
}

// Hook for signing contracts
export const useSignContract = () => {
    const { toast } = useToast();

    return useMutation<SignContractResponse, Error, SignContractRequest>({
        mutationFn: async (signData: SignContractRequest) => {
            // Must go through apiClient so Authorization uses access_token
            // (localStorage key `token` is unused and produced Bearer null → 401).
            return apiClient.post<SignContractResponse>(
                '/contracts/sign',
                buildStaffSignFormData(signData),
            );
        },
        onSuccess: () => {
            toast({
                title: "Contract Signed",
                description: "Contract has been successfully signed and submitted.",
            });
        },
        onError: (error) => {
            console.error('Failed to sign contract:', error);
            toast({
                title: "Error",
                description: describeApiError(error, "Failed to sign contract. Please try again."),
                variant: "destructive",
            });
        },
    });
};

export const useMarkContractLost = () => {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<{ success: boolean; status: string }, Error, string>({
        mutationFn: async (contractId: string) => {
            return apiClient.post(`/contracts/${contractId}/mark-lost`);
        },
        onSuccess: (_data, contractId) => {
            queryClient.invalidateQueries({ queryKey: ['contracts-list'] });
            queryClient.invalidateQueries({ queryKey: ['contract-details', contractId] });
            queryClient.invalidateQueries({ queryKey: ['contracts-by-client'] });
            toast({
                title: 'Marked lost',
                description: 'This contract is now inactive.',
            });
        },
        onError: (error) => {
            toast({
                title: 'Could not mark lost',
                description: error instanceof Error ? error.message : 'Please try again.',
                variant: 'destructive',
            });
        },
    });
};

export const useMarkContractWon = () => {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<{ success: boolean; status: string }, Error, string>({
        mutationFn: async (contractId: string) => {
            return apiClient.post(`/contracts/${contractId}/mark-won`);
        },
        onSuccess: (_data, contractId) => {
            queryClient.invalidateQueries({ queryKey: ['contracts-list'] });
            queryClient.invalidateQueries({ queryKey: ['contract-details', contractId] });
            queryClient.invalidateQueries({ queryKey: ['contracts-by-client'] });
            toast({
                title: 'Marked won',
                description: 'This contract is now in the won group.',
            });
        },
        onError: (error) => {
            toast({
                title: 'Could not mark won',
                description: error instanceof Error ? error.message : 'Please try again.',
                variant: 'destructive',
            });
        },
    });
};

// Hook for updating contract legal language and/or warranty
export const useUpdateContractLegal = () => {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<
        { success: boolean; contract: { id: string; contractLanguage: string | null; limitedLiabilityWarranty: string | null } },
        Error,
        { contractId: string; contractLanguage?: string; limitedLiabilityWarranty?: string }
    >({
        mutationFn: async ({ contractId, ...payload }) => {
            return apiClient.patch(`/contracts/${contractId}/legal`, payload);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['contract-details', variables.contractId] });
            toast({
                title: 'Saved',
                description: 'Contract legal fields updated.',
            });
        },
        onError: (error) => {
            console.error('Failed to update contract legal:', error);
            toast({
                title: 'Error',
                description: 'Failed to save legal changes.',
                variant: 'destructive',
            });
        },
    });
};

export const useUpdateContractPaymentInstructions = () => {
    const { toast } = useToast();

    return useMutation<
        { success: boolean; payment: { downPaymentMethod: string | null; checkNumber: string | null } },
        Error,
        { contractId: string; downPaymentMethod?: string | null; checkNumber?: string | null }
    >({
        mutationFn: async ({ contractId, ...payload }) => {
            return apiClient.patch(`/contracts/${contractId}/payment-instructions`, payload);
        },
        onSuccess: () => {
            toast({
                title: 'Saved',
                description: 'Down-payment instructions updated.',
            });
        },
        onError: (error) => {
            console.error('Failed to update payment instructions:', error);
            toast({
                title: 'Error',
                description: 'Failed to save down-payment instructions.',
                variant: 'destructive',
            });
        },
    });
};

export const useUpdateContractDecisions = () => {
    const { toast } = useToast();

    return useMutation<
        { success: boolean; decisions: Record<string, unknown>; complete: boolean },
        Error,
        {
            contractId: string;
            financingProgramId?: string | null;
            financingConfirmation?: string | null;
            financingApplication?: {
                name?: string;
                email?: string;
                phone?: string;
                income?: string;
            } | null;
            downPaymentKind?: 'nothing' | 'amount' | null;
            downPaymentAmount?: number | null;
            downPaymentMethod?: string | null;
            checkNumber?: string | null;
            cardConfirmation?: string | null;
            cardLast4?: string | null;
        }
    >({
        mutationFn: async ({ contractId, ...payload }) => {
            return apiClient.patch(`/contracts/${contractId}/decisions`, payload);
        },
        onSuccess: () => {
            toast({
                title: 'Saved',
                description: 'Contract decisions updated.',
            });
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error instanceof Error ? error.message : 'Failed to save decisions.',
                variant: 'destructive',
            });
        },
    });
};

// Hook for fetching contracts by client ID
export const useContractsByClient = (clientId: string | null) => {
    return useQuery<any[], Error>({
        queryKey: ['contracts-by-client', clientId],
        queryFn: async () => {
            const response = await apiClient.get<any[]>(`/contracts/client/${clientId}`);
            return response;
        },
        enabled: !!clientId,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });
};

/** POST /contracts/:id/generate-summary — fills the contract overview narrative. */
export async function generateContractNarrative(
  contractId: string,
): Promise<{ summary: string }> {
  return apiClient.post<{ summary: string }>(
    `/contracts/${encodeURIComponent(contractId)}/generate-summary`,
    {},
  );
}

export const useGenerateContractNarrative = () => {
  const { toast } = useToast();

  return useMutation<{ summary: string }, Error, string>({
    mutationFn: generateContractNarrative,
    onError: (error) => {
      toast({
        title: 'Could not generate narrative',
        description: describeApiError(
          error,
          'AI narrative generation failed. If this is production, confirm ANTHROPIC_API_KEY is set on the backend Elastic Beanstalk environment.',
        ),
        variant: 'destructive',
      });
    },
  });
};

// Hook for generating the locked contract PDF (by contract UUID).
export const useGenerateContractPDF = () => {
    const { toast } = useToast();

    return useMutation<
        GenerateContractPDFResponse,
        Error,
        GenerateContractPdfByIdRequest | GenerateContractPDFRequest
    >({
        mutationFn: async (pdfData) => {
            if ('contractId' in pdfData && pdfData.contractId) {
                return apiClient.post<GenerateContractPDFResponse>(
                    `/contracts/${encodeURIComponent(pdfData.contractId)}/generate-pdf`,
                );
            }
            return apiClient.post<GenerateContractPDFResponse>(
                '/contracts/generate-pdf',
                pdfData,
            );
        },
        onError: (error) => {
            console.error('Failed to generate contract PDF:', error);
            toast({
                title: 'Could not generate contract PDF',
                description:
                    error instanceof Error
                        ? error.message
                        : 'Failed to generate the contract document. Please try again.',
                variant: 'destructive',
            });
        },
    });
};

/** Authenticated stream of the stored contract PDF (inline). */
export async function fetchContractPdfBlob(contractId: string): Promise<Blob> {
    const token = tokenStorage.getToken();
    const url = `${API_BASE_URL}/contracts/${encodeURIComponent(contractId)}/pdf`;
    const res = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
        const raw = await res.text().catch(() => '');
        let detail = raw;
        try {
            const parsed = JSON.parse(raw) as { message?: string };
            if (parsed?.message) detail = parsed.message;
        } catch {
            /* keep raw */
        }
        throw new Error(detail || `Failed to load the contract PDF (${res.status})`);
    }
    return res.blob();
}